import { useMutation } from '@tanstack/react-query';
import { axiosClient } from '../axiosClient';

interface ResidenceType {
  id: number;
  address?: string;
  wgs84lat: number;
  wgs84lon: number;
  [key: string]: string | number | undefined;
}

interface RegistryResponseType {
  residences: ResidenceType[];
  meta_data?: {
    limit: number;
    offset: number;
    response_rows: number;
    total_nr_records: number;
  };
}

interface RegistryProgressType {
  fetched: number;
  total: number | null;
}

interface RegistrySearchArgsType {
  filters: Record<string, string[]>;
  limit: number;
  fetchAll: boolean;
  onProgress?: (progress: RegistryProgressType) => void;
}

// Form fields use friendlier names than the API; remap before sending
const API_FIELD_NAMES: Record<string, string> = {
  street: 'address',
  residential_ids: 'id',
};

// Multiple values per field are OR'ed by running one request per value
// combination; results are merged on residence id
const MAX_COMBINATIONS = 50;
const PAGE_SIZE = 500;
const MAX_TOTAL = 20000;

const TOO_MANY_COMBINATIONS = 'TOO_MANY_COMBINATIONS';

const buildCombinations = (filters: Record<string, string[]>) => {
  let combinations: Record<string, string>[] = [{}];

  Object.entries(filters)
    .filter(([, values]) => values.length)
    .forEach(([key, values]) => {
      combinations = combinations.flatMap((combination) =>
        values.map((value) => ({ ...combination, [key]: value }))
      );
      if (combinations.length > MAX_COMBINATIONS) {
        throw new Error(TOO_MANY_COMBINATIONS);
      }
    });

  return combinations;
};

const fetchPage = async (queryParts: string[]) => {
  const URL = `${import.meta.env.VITE_API_REGISTRY_URL}?${queryParts.join('&')}`;
  const { data } = await axiosClient.get<RegistryResponseType>(URL);
  return {
    residences: data?.residences ?? [],
    total: data?.meta_data?.total_nr_records ?? null,
  };
};

const useRegistrySearch = () => {
  return useMutation({
    mutationFn: async ({
      filters,
      limit,
      fetchAll,
      onProgress,
    }: RegistrySearchArgsType): Promise<ResidenceType[]> => {
      const combinations = buildCombinations(filters);
      const byId = new Map<number, ResidenceType>();

      // Progress counts raw rows across combinations; totals come from the
      // meta_data of each combination's first page
      let fetchedRows = 0;
      let knownTotal = 0;
      let allTotalsKnown = true;

      for (const combination of combinations) {
        const baseParts = Object.entries(combination).map(
          ([key, value]) =>
            `${API_FIELD_NAMES[key] ?? key}=${encodeURIComponent(value)}`
        );

        if (fetchAll) {
          let offset = 0;
          let comboTotalCounted = false;
          for (;;) {
            const { residences, total } = await fetchPage([
              ...baseParts,
              `limit=${PAGE_SIZE}`,
              `offset=${offset}`,
            ]);
            residences.forEach((residence) =>
              byId.set(residence.id, residence)
            );

            fetchedRows += residences.length;
            if (!comboTotalCounted) {
              comboTotalCounted = true;
              if (total === null) allTotalsKnown = false;
              else knownTotal += Math.min(total, MAX_TOTAL);
            }
            onProgress?.({
              fetched: fetchedRows,
              total: allTotalsKnown ? knownTotal : null,
            });

            if (residences.length < PAGE_SIZE || byId.size >= MAX_TOTAL) break;
            offset += PAGE_SIZE;
          }
        } else {
          const { residences } = await fetchPage([
            ...baseParts,
            `limit=${limit}`,
          ]);
          residences.forEach((residence) => byId.set(residence.id, residence));
        }
      }

      return Array.from(byId.values());
    },
  });
};

export default useRegistrySearch;
export { TOO_MANY_COMBINATIONS };
export type { ResidenceType, RegistryProgressType };
